import React, { createContext, useContext, useEffect, useState } from "react";
import { useIdleTimer } from "react-idle-timer";
import { ThemeProvider } from "styled-components";
import { darkTheme, lightTheme } from "@themes/Theme";
import { FOCUS_MODE_DEFAULT_TIMEOUT_MS } from "@user/components/UserSettings/AppearanceSettingsConstants";
import { fetchLightModeSetting } from "@user/service/UserSettingsService";
import { useUserContext } from "./UserContext";

export type AppearanceContextType = {
    // Theme
    lightMode: boolean;
    setLightMode: (mode: boolean) => void;

    // Focus Mode
    isFocusModeActive?: boolean;
    setIsFocusModeActive?: (active: boolean) => void;
    focusModeEnabled?: boolean;
    setFocusModeEnabled?: (enabled: boolean) => void;
    focusModeIdleTime?: number;
    setFocusModeIdleTime?: (idleMs: number) => void;
};

export const AppearanceContext: React.Context<AppearanceContextType> = createContext<AppearanceContextType>({
    // Theme
    lightMode: true,
    setLightMode: () => {},

    // Focus Mode
    isFocusModeActive: false,
    setIsFocusModeActive: () => {},
    focusModeEnabled: true,
    setFocusModeEnabled: () => {},
    focusModeIdleTime: FOCUS_MODE_DEFAULT_TIMEOUT_MS,
    setFocusModeIdleTime: () => {},
});

interface AppearanceContextProviderProps {
    children?: React.ReactNode;
}

export function AppearanceContextProvider({ children }: AppearanceContextProviderProps) {
    const { user, userData } = useUserContext();
    // Respects system settings for light/dark mode preference
    const [lightMode, setLightMode] = useState<boolean>(fetchLightModeSetting(userData));

    const [isFocusModeActive, setIsFocusModeActive] = useState(false);
    const [focusModeEnabled, setFocusModeEnabled] = useState<boolean>(userData?.focusModeEnabled || true);
    const [focusModeIdleTime, setFocusModeIdleTime] = useState<number>(
        userData?.focusModeIdleTimeout || FOCUS_MODE_DEFAULT_TIMEOUT_MS,
    );

    const lightModeTheme = {
        lightMode: lightMode,
        setLightMode: () => setLightMode(!lightMode),
        isFocusModeActive: isFocusModeActive,
        setIsFocusModeActive: (active: boolean) => setIsFocusModeActive(active),
        focusModeEnabled: focusModeEnabled,
        setFocusModeEnabled: (enabled: boolean) => setFocusModeEnabled(enabled),
        focusModeIdleTime: focusModeIdleTime,
        setFocusModeIdleTime: (ms: number) => setFocusModeIdleTime(ms),
    };

    // Once the user data is detected, update the appearance settings values.
    useEffect(() => {
        if (user && userData) {
            setLightMode(userData.lightMode);
            setFocusModeEnabled(userData.focusModeEnabled);
            setFocusModeIdleTime(userData.focusModeIdleTimeout);
        }
    }, [user, userData]);

    useIdleTimer({
        timeout: focusModeIdleTime || FOCUS_MODE_DEFAULT_TIMEOUT_MS,
        onIdle: handleEnterFocusMode,
        onActive: handleExitFocusMode,
        startOnMount: false,
    });

    function handleEnterFocusMode() {
        if (focusModeEnabled) {
            setIsFocusModeActive(true);
        }
    }

    function handleExitFocusMode() {
        if (focusModeEnabled) {
            setIsFocusModeActive(false);
        }
    }

    return (
        <AppearanceContext.Provider value={lightModeTheme}>
            <ThemeProvider theme={lightMode ? lightTheme : darkTheme}>{children}</ThemeProvider>
        </AppearanceContext.Provider>
    );
}

export const useAppearanceContext = () => useContext(AppearanceContext);

export default AppearanceContext;
